// src/modules/app-views/_apps/appmulti/quotations/Form/Steps/CheckoutStep1.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useRef, useState } from 'react';
import { Button, Card, Col, Flex, Form, Input, Row, Space, Table } from 'antd';
import {
  AlertOutlined,
  DeleteOutlined,
  EditOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { parseCpfToDisplay, sort } from 'utils/helpers';
import { formatPhoneNumberByCountry } from 'utils/phoneFormatter';
import translateX from 'utils/translateX';
import appService from 'services/appService';
import axiosService from 'services/axiosService';
import {
  SimpleFormProvider,
  useSimpleFormContext,
} from 'components/SimpleComponents/SimpleFormProvider';
import SimpleButton from 'components/SimpleComponents/SimpleButton';
import SimpleAvatar from 'components/SimpleComponents/SimpleAvatar';
import Icon from 'components/JarvislyComponents/Icon';
import { API } from 'constants/Api';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import MultiQuotationsOrderPassengerModal from './Modals/PassengerModal';

// *******************************************************************************
// GLOBAL VARIABLES
// *******************************************************************************
const applyBrPhoneMask = e => {
  const raw = e?.target?.value || '';
  const digits = raw.replace(/\D/g, '');
  const { formatted } = formatPhoneNumberByCountry(digits, { countryCode: 'BR' });
  return formatted || digits;
};

const removeBtnStyle = { marginBottom: 24 };
const emergencyTitleStyle = { color: 'darkcyan' };
const emergencyTitleTextStyle = { marginLeft: 8 };
const passengerColWidthStyle = { width: 220 };
const passengerSubtitleStyle = { fontSize: 12 };
const addPassengerBtnStyle = { width: 130, margin: 16 };
const nextBtnStyle = { width: 130 };

// *******************************************************************************
// COMPONENT — EmergencyForm (inside its own provider)
// *******************************************************************************
const EmergencyForm = props => {
  // HOOKS
  const { form, goFocus } = useSimpleFormContext();
  props.registerSubmit?.(form.submit);

  // RENDER
  return (
    <Form.List name="emergencyContacts">
      {(fields, { add, remove }) => (
        <>
          {fields.map(({ key, name, ...restField }) => (
            <Row gutter={ROW_GUTTER} align="bottom" key={key}>
              <Col xs={24} md={13}>
                <Form.Item
                  {...restField}
                  name={[name, 'name']}
                  rules={[{ required: true, message: '' }]}
                >
                  <Input placeholder={translateX('name')} />
                </Form.Item>
              </Col>
              <Col xs={24} md={9}>
                <Form.Item
                  {...restField}
                  name={[name, 'phone']}
                  rules={[{ required: true, message: '' }]}
                  getValueFromEvent={applyBrPhoneMask}
                >
                  <Input placeholder={translateX('phone')} />
                </Form.Item>
              </Col>
              <Col xs={24} md={2}>
                <Button
                  style={removeBtnStyle}
                  disabled={fields.length <= 1}
                  icon={<MinusCircleOutlined />}
                  onClick={() => remove(name)}
                />
              </Col>
            </Row>
          ))}
          <Button
            type="dashed"
            onClick={() => {
              add();
              setTimeout(() => goFocus([fields.length, 'name'].join(',')), 0);
            }}
            block
            icon={<PlusOutlined />}
          >
            {translateX('contact')}
          </Button>
        </>
      )}
    </Form.List>
  );
};

// *******************************************************************************
// COMPONENT — main
// *******************************************************************************
const MultiQuotationsCheckoutStep1 = props => {
  // HOOKS
  const {
    isWorking,
    setStep,
    documentContext,
    travellers,
    setTravellers,
    setEnabledSteps,
    enabledSteps,
    buyer,
    setBuyer,
    isMobile,
    setUpdating,
    isUpdating,
    isDisabled,
    setEmergencyContacts,
  } = props;

  const { filter } = documentContext;
  const submitEmergencyRef = useRef(() => {});
  const unavailable =
    isWorking ||
    !travellers ||
    travellers.length === 0 ||
    travellers.find(t => !t.cpf && !t.noCpf);

  // STATE
  const [showPassengerModal, setShowPassengerModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  // HANDLERS
  const onAddPassenger = () => {
    setSelectedRecord(null);
    if (!filter?.destiny || !filter?.departure || !filter?.arrival) {
      appService.message({ flag: 'e', text: 'incomplete_data' });
      return;
    }
    setShowPassengerModal(true);
  };

  const onEditPassenger = record => {
    if (!filter?.destiny || !filter?.departure || !filter?.arrival) {
      appService.message({ flag: 'e', text: 'incomplete_data' });
      return;
    }
    setSelectedRecord(record);
    setShowPassengerModal(true);
  };

  const onDeletePassenger = value =>
    setTravellers([...travellers.filter(t => t?.key !== value.key)]);

  const onModalCallbackFn = async data => {
    setSelectedRecord(null);
    if (!data || Object.keys(data).length === 0 || isDisabled) return;
    const found = travellers.find(t => t.key === data?.key);
    const p = found
      ? [...travellers.filter(t => t.key !== data.key), data].sort((a, b) =>
          sort(a, b, 'key'),
        )
      : [...travellers, data].sort((a, b) => sort(a, b, 'key'));
    setTravellers(p);
  };

  const onNext = async () => {
    if (isDisabled) return setStep(1);
    const complete = travellers.every(t => t.entityId && t.cpf);
    if (!complete) {
      appService.notification({
        flag: 'w',
        title: 'not_allowed',
        description: 'message_complete_passenger',
        duration: 10,
      });
      return;
    }
    setUpdating(true);
    const b = buyer ? { ...buyer } : {};
    if (!b?.cpf && travellers[0]?.entityId) {
      const [doc] = await axiosService.get({
        url: API.ENTITIES.URL.replace(':_id', travellers[0].entityId),
        returnAsObject: true,
      });
      setBuyer({ ...doc });
    }
    submitEmergencyRef.current();
  };

  const onConfirmEmergencyForm = values => {
    setEmergencyContacts(values?.emergencyContacts);
    setUpdating(false);
    setStep(1);
    if (enabledSteps && !enabledSteps.includes(1)) {
      setEnabledSteps([...enabledSteps, 1]);
    }
    return false; // abort provider auto-save
  };

  // RENDER
  const tableColumns = [
    {
      title: translateX('passengers'),
      dataIndex: 'name',
      className: 'text-truncate',
      render: (_, record) => {
        const age = record?.birthdate
          ? dayjs(filter?.arrival).diff(dayjs(record.birthdate), 'years')
          : record?.age;
        return (
          <Flex align="center" style={passengerColWidthStyle} gap={8}>
            <SimpleAvatar
              shape="circle"
              size="small"
              name={record?.name || record?.fullName}
              foreColor={record?.foreColor}
              backColor={record?.backColor}
            />
            <div>
              <div className="text-truncate">
                {record?.name || record?.fullName}
              </div>
              <div className="text-muted" style={passengerSubtitleStyle}>
                {parseCpfToDisplay(record?.cpf)}
                {typeof age === 'number'
                  ? ` · ${age} ${translateX('years_old')}`
                  : ''}
              </div>
            </div>
          </Flex>
        );
      },
    },
    {
      title: translateX('actions'),
      dataIndex: 'actions',
      width: 120,
      className: 'text-center',
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            disabled={isDisabled}
            onClick={() => onEditPassenger(record)}
          />
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            disabled={isDisabled}
            onClick={() => onDeletePassenger(record)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div className="w-100">
      <MultiQuotationsOrderPassengerModal
        {...props}
        onCallbackFn={onModalCallbackFn}
        record={selectedRecord}
        showModal={showPassengerModal}
        setShowModal={setShowPassengerModal}
        isWorking={isWorking}
        travellers={travellers}
        setSelectedRecord={setSelectedRecord}
      />

      <Row gutter={ROW_GUTTER}>
        <Col xs={24} lg={12}>
          <Card className="w-100 strong-border cad-with-body-no-padding">
            <Table
              rowKey="key"
              className="hide-last-line-table"
              columns={tableColumns}
              dataSource={travellers}
              pagination={false}
              loading={isWorking}
            />
            <Flex justify="center">
              <SimpleButton
                title="passenger"
                icon={<PlusOutlined />}
                style={addPassengerBtnStyle}
                className="btn-add-passenger"
                onClick={onAddPassenger}
                disabled={isUpdating}
              />
            </Flex>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title={
              <Flex align="center" style={emergencyTitleStyle}>
                <div>
                  <Icon type={AlertOutlined} />
                </div>
                <div style={emergencyTitleTextStyle}>
                  {translateX('emergency_contacts')}
                </div>
              </Flex>
            }
            className="w-100 strong-border"
          >
            <SimpleFormProvider
              name="emergency-form"
              showSubmitButton={false}
              requiredFields={[]}
              onFinish={onConfirmEmergencyForm}
              initialValues={{
                emergencyContacts: props.emergencyContacts || [
                  { name: '', phone: '' },
                ],
              }}
            >
              <EmergencyForm
                {...props}
                registerSubmit={fn => (submitEmergencyRef.current = fn)}
              />
            </SimpleFormProvider>
          </Card>
        </Col>
      </Row>

      {!isDisabled ? (
        <Flex justify={isMobile ? 'center' : 'flex-end'} align="center">
          <SimpleButton
            title="next"
            type={travellers?.length > 0 ? 'primary' : 'default'}
            icon={<SaveOutlined />}
            disabled={unavailable || isUpdating}
            onClick={onNext}
            style={nextBtnStyle}
          />
        </Flex>
      ) : null}
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default MultiQuotationsCheckoutStep1;
